<template>
  <div class="ins-gr-segbar">
    <!-- строка 0: созданные сборки. Без неё переключаться между ними можно было бы
         только кликом по строке, а понять, сколько их всего, было бы нечем. -->
    <div v-if="assemblies.length" class="ins-gr-segrow">
      <span class="tlabel ins-gr-seglab">сборки</span>
      <button v-for="a in assemblies" :key="a.id" type="button" class="ins-gr-segtab"
              :aria-pressed="String(editId === a.id)" :title="tabTitle(a)" @click="$emit('tab', a.id)">{{ a.name }}</button>
      <button type="button" class="ins-gr-segtab ins-gr-segnew" @click="onNew"
              title="Начать следующую сборку с чистого листа: состав, имя и поиск сбрасываются, уже созданные строки остаются.">+ новая</button>
    </div>

    <!-- строка 1: название -->
    <div class="ins-gr-segrow">
      <span class="tlabel ins-gr-seglab">название строки</span>
      <input class="btn ins-gr-segin" :maxlength="nameMax" :value="nameInput" :placeholder="placeholder"
             :title="'Как строка подпишется в таблице. Пустое поле возвращает автоматическое имя, собранное из состава.' + (fullName ? ' Полное имя состава: ' + fullName : '')"
             @input="onName" @blur="nameInput = nameInput.trim()" @keydown.enter="onNameEnter">
      <span class="ins-gr-seglen num" :class="{ 'is-warn': lenUsed > nameMax }" :title="lenTitle">{{ lenText }}</span>
      <button v-if="editId === null" type="button" class="btn ins-gr-segact" :disabled="!atoms.length"
              title="Ставит набранную сборку в таблицу отдельной строкой." @click="commit">добавить строку</button>
      <!-- выходим из правки, но режим набора не гасим: следующая сборка собирается сразу -->
      <button v-else type="button" class="btn ins-gr-segact" @click="$emit('done')"
              title="Закрывает правку. Режим набора остаётся включённым: можно собирать следующую.">готово</button>
    </div>

    <!-- строка 2: поиск и «вся таблица» -->
    <div class="ins-gr-segrow">
      <span class="tlabel ins-gr-seglab">{{ editId !== null ? 'правка состава' : 'состав' }}</span>
      <input class="btn ins-gr-segin ins-gr-segfind" v-model="find"
             placeholder="+ фраза: ищи по тем, которых тут ещё нет" @keydown.enter="onFindEnter"
             title="Полнотекстовый поиск по связке «заголовок + подзаголовок + фраза»: каждое набранное слово должно найтись. Enter кладёт первую подсказку.">
      <button type="button" class="btn" title="Одна сборка по всей таблице, частый случай. Повторный клик снимает."
              @click="$emit('atoms', full ? [] : every.map(pick))">{{ full ? 'снять всё' : 'вся таблица' }}</button>
      <span class="ins-gr-seghit">
        {{ editId !== null ? 'правишь имя и состав строки' : 'кликай по строкам таблицы: строка войдёт целиком' }}
      </span>
    </div>

    <!-- строка 3: что набрано. Каркас выше не пересоздаётся при правке состава,
         иначе поле ввода теряло бы фокус после каждой добавленной фразы. -->
    <div class="ins-gr-segrow">
      <template v-if="query">
        <span class="ins-gr-segsug">
          <span class="tlabel">{{ hits.length ? 'новые:' : 'ничего нового не нашлось' }}</span>
          <button v-for="(p, i) in hits.slice(0, 10)" :key="ak(p)" type="button"
                  class="ins-gr-segadd" :class="{ 'is-top': i === 0 }"
                  @click="$emit('atoms', atoms.concat([pick(p)]))">
            <i :style="{ background: dot(p) }"></i><b>{{ p.label }}</b> {{ p.phrase }}
          </button>
          <span v-if="hits.length > 10" class="tlabel">и ещё {{ hits.length - 10 }}</span>
        </span>
        <!-- Весь набранный состав, а не только совпавшее: поиск не должен прятать то,
             что уже накликано по таблице. Совпавшие подсвечены. -->
        <span v-if="atoms.length" class="ins-gr-segsug is-had">
          <span class="tlabel">
            уже добавлены ({{ atoms.length }}){{ mine.size ? ', совпало ' + mine.size : '' }}:
          </span>
          <span v-for="p in ordered" :key="ak(p)" class="ins-gr-segchip" :class="{ 'is-hit': mine.has(ak(p)) }">
            <i :style="{ background: dot(p) }"></i><b>{{ p.label }}</b>
            <span class="ins-gr-segph">{{ p.phrase }}</span>
            <button type="button" class="ins-gr-segx" title="Убрать эту фразу из состава" aria-label="убрать" @click="remove(p)">×</button>
          </span>
        </span>
      </template>
      <template v-else>
        <span v-if="!atoms.length" class="tlabel">пусто: кликай по строкам таблицы или ищи фразу</span>
        <span v-for="p in ordered" :key="ak(p)" class="ins-gr-segchip">
          <i :style="{ background: dot(p) }"></i><b>{{ p.label }}</b>
          <span class="ins-gr-segph">{{ p.phrase }}</span>
          <button type="button" class="ins-gr-segx" title="Убрать эту фразу из состава" aria-label="убрать" @click="remove(p)">×</button>
        </span>
      </template>
      <span v-if="atoms.length" class="ins-gr-seghit">{{ caught }}</span>
    </div>
  </div>
</template>

<script>
import { plural, segMask } from '@/utils/segments';

// Атом сборки: {text: ключ текста, phrase, word?}. Ключ пары — тот же JSON, что в assemblyCount.
const ak = p => JSON.stringify([p.text, p.phrase]);

export default {
  name: 'HeadGridSegBar',
  props: {
    assemblies: { type: Array, required: true },     // [{id, name, auto, pin, atoms}]
    editId: { type: String, default: null },         // id сборки на правке или null: набираем новую
    atoms: { type: Array, required: true },          // состав правимой сборки или черновика
    every: { type: Array, required: true },          // все пары таблицы [{text, phrase, label, sub, k}]
    fullName: { type: String, default: '' },         // полное имя состава (считает HeadGrid)
    shortName: { type: String, default: '' },        // короткое имя состава
    nameMax: { type: Number, required: true },
    tabTitle: { type: Function, default: a => a.name },          // полное автоимя состава сборки
    phColor: { type: Function, default: () => 'var(--ph0)' },    // (атом) → цвет фразы как в слоях
  },
  emits: ['tab', 'new', 'rename', 'commit', 'done', 'atoms'],
  data() {
    // draftName: имя ещё не добавленной строки, переживает заход во вкладку готовой сборки (segName в П)
    return { nameInput: '', draftName: '', find: '' };
  },
  computed: {
    editAsm() {
      return this.editId === null ? null : this.assemblies.find(a => a.id === this.editId) || null;
    },
    query() {
      return this.find.trim();
    },
    index() {
      const m = new Map();
      this.every.forEach((p, i) => m.set(ak(p), i));
      return m;
    },
    full() {
      return this.atoms.length > 0 && new Set(this.atoms.map(ak)).size >= this.every.length;
    },
    // Поиск предлагает только то, чего в составе ещё нет: искать уже добавленное бессмысленно.
    matched() {
      if (!this.query) return [];
      return this.every.filter(p => segMask(this.query, p.label + ' ' + p.sub + ' ' + p.phrase));
    },
    hits() {
      const have = new Set(this.atoms.map(ak));
      return this.matched.filter(p => !have.has(ak(p)));
    },
    mine() {
      const got = new Set(this.matched.map(ak));
      return new Set(this.atoms.map(ak).filter(k => got.has(k)));
    },
    ordered() {
      const at = p => (this.index.has(ak(p)) ? this.index.get(ak(p)) : Infinity);
      // Слово атома (по какой строке кликнули) важнее слова из every: от него цвет точки.
      return this.atoms.map(p => this.index.has(ak(p)) ? { ...this.every[this.index.get(ak(p))], word: p.word } : { ...p, label: '?', sub: '', k: 0 })
        .sort((a, b) => at(a) - at(b));
    },
    // Короткое имя идёт подсказкой, а не значением: записанное упиралось бы в предел,
    // и печатать своё стало бы нечем. Пустое поле = это имя.
    placeholder() {
      return this.shortName || 'название строки';
    },
    lenUsed() {
      return (this.nameInput || this.placeholder || '').length;
    },
    lenText() {
      return this.atoms.length || this.nameInput ? this.lenUsed + '/' + this.nameMax : '';
    },
    lenTitle() {
      return 'Предел ' + this.nameMax + ' знаков: по самому длинному заголовку в данных.'
        + (this.fullName.length > this.nameMax
          ? ' Полное имя состава «' + this.fullName + '» длиннее и в колонку не влезет.' : '');
    },
    caught() {
      const n = this.atoms.length;
      const w = new Set(this.atoms.map(p => p.text)).size;
      return 'поймано: ' + n + ' ' + plural(n, ['фраза', 'фразы', 'фраз'])
        + ' в ' + w + ' ' + plural(w, ['заголовке', 'заголовках', 'заголовках']);
    },
  },
  watch: {
    // Сменилась сборка на правке: поле имени показывает её имя (своё, не авто).
    editId: {
      immediate: true,
      handler() {
        this.nameInput = this.editId === null ? this.draftName : this.editAsm && !this.editAsm.auto ? this.editAsm.name : '';
        this.find = '';
      },
    },
  },
  methods: {
    ak,
    pick: p => ({ text: p.text, phrase: p.phrase, word: p.word }),   // word только ради цвета точки
    dot(p) {
      return this.phColor(p);
    },
    remove(p) {
      const k = ak(p);
      this.$emit('atoms', this.atoms.filter(q => ak(q) !== k));
    },
    onNew() {
      this.nameInput = this.draftName = '';
      this.find = '';
      this.$emit('new');
    },
    onName(event) {
      const v = event.target.value.slice(0, this.nameMax);
      this.nameInput = v;
      if (this.editId !== null) this.$emit('rename', v.trim());
      else this.draftName = v;
    },
    onNameEnter() {
      if (this.editId === null) this.commit();
    },
    commit() {
      if (!this.atoms.length) return;
      this.$emit('commit', this.nameInput.trim());
      this.nameInput = this.draftName = '';
      this.find = '';
    },
    // Enter кладёт первую подсказку и очищает поле: готово к следующей.
    onFindEnter() {
      const h = this.hits[0];
      if (!h) return;
      this.find = '';
      this.$emit('atoms', this.atoms.concat([this.pick(h)]));
    },
  },
};
</script>

<style>
/* Не scoped: классы с префиксом ins-gr-, а bootstrap перекрывает короткие имена даже при scoped. */
.ins-root .ins-gr-segbar{display:flex; flex-direction:column; gap:9px; margin:0 0 14px; padding:11px 13px;
  border:1px dashed var(--grid); border-radius:11px; background:var(--gr-s2); font-size:12.5px;}
.ins-root .ins-gr-segrow{display:flex; flex-wrap:wrap; align-items:center; gap:8px;}
.ins-root .ins-gr-seglab{min-width:112px;}
/* input.btn: общее .ins-root input.btn{min-width:0} (insights.css) и .btn центрируют и сжимают поле,
   поэтому селектор длиннее. Ширины как в прототипе: имя 216 (min-width 200 плюс size=20 в П), поиск 250. */
.ins-root .ins-gr-segbar input.ins-gr-segin{min-width:216px; padding:5px 9px; font-size:13px; border-radius:8px; line-height:1.5; text-align:left; cursor:text;}
.ins-root .ins-gr-segbar input.ins-gr-segfind{min-width:250px;}
.ins-root .ins-gr-segact{margin-inline-start:auto;}
.ins-root .ins-gr-segbar .btn{padding:5px 10px; border-color:var(--grid); line-height:1.5;}
.ins-root .ins-gr-segtab{font:inherit; font-size:12.5px; padding:3px 10px; border-radius:999px; cursor:pointer; white-space:nowrap;
  border:1px solid var(--grid); background:var(--surface-1); color:var(--text-primary); max-width:230px; overflow:hidden; text-overflow:ellipsis;}
.ins-root .ins-gr-segtab[aria-pressed="true"]{border-color:var(--accent); color:var(--accent); font-weight:600;}
.ins-root .ins-gr-segnew{border-style:dashed;}
.ins-root .ins-gr-seglen{min-width:44px; color:var(--text-muted); font-size:12px;}
.ins-root .ins-gr-seglen.is-warn{color:var(--div-pos); font-weight:700;}
.ins-root .ins-gr-seghit{color:var(--text-muted); font-size:12px;}
.ins-root .ins-gr-segchip{display:inline-flex; align-items:center; gap:5px; padding:2px 7px; border-radius:999px;
  border:1px solid var(--grid); background:var(--surface-1); font-size:12px; max-width:420px;}
.ins-root .ins-gr-segchip.is-hit{border-color:var(--accent);}
.ins-root .ins-gr-segchip.is-hit b{color:var(--accent);}
.ins-root .ins-gr-segchip i, .ins-root .ins-gr-segadd i{width:8px; height:8px; border-radius:50%; display:block; flex:0 0 auto;}
.ins-root .ins-gr-segchip b, .ins-root .ins-gr-segadd b{font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:200px;}
.ins-root .ins-gr-segchip .ins-gr-segph{color:var(--text-muted);}
.ins-root .ins-gr-segx{border:0; background:none; padding:0; font:inherit; color:var(--text-muted); cursor:pointer; font-size:13px; line-height:1;}
.ins-root .ins-gr-segx:hover{color:var(--text-primary);}
.ins-root .ins-gr-segsug{display:flex; flex-wrap:wrap; gap:5px; align-items:center;}
.ins-root .ins-gr-segsug.is-had{border-top:1px dashed var(--grid); padding-top:7px; margin-top:2px; width:100%;}
.ins-root .ins-gr-segadd{display:inline-flex; align-items:center; gap:5px; font:inherit; font-size:12px; cursor:pointer;
  padding:2px 8px; border-radius:999px; border:1px dashed var(--grid); background:var(--surface-1); color:var(--text-primary);}
.ins-root .ins-gr-segadd:hover{border-style:solid; border-color:var(--accent);}
.ins-root .ins-gr-segadd.is-top{border-color:var(--accent);}
</style>
